/**
 * PV/CK y atributos de combate derivados
 * (mismas fórmulas que server/utils/characterDerivedStats.js).
 *
 * PV = floor( (50 + FUE*1.5 + AGI*1.5 + RES*2) * (1 + NIVEL/20) + clanBonusPV )
 * CK = floor( 100 + INT*1.5 + VOL*1.5 + CdC + CON + NIVEL/10 + clanBonusCK )
 *
 * Atributos de combate (totales base+bonus):
 * - Precisión:          DES×2+(FUE/5)     (acierto cuerpo a cuerpo)
 * - Puntería:           DES×2+(CON/5)     (acierto con proyectiles)
 * - Aguante:            VOL×2+(RES/2)     (resistencia a estados alterados)
 * - Enfoque:            CON×2+(VOL/5)+(INT/5)
 * - Salto vertical:     (FUE×0.5)+(AGI×1.5) m
 * - Salto horizontal:   (FUE×0.3)+(AGI×2) m
 * - Peso máximo:        (FUE×5)+(RES×2) kg
 * - Velocidad carrera:  AGI×2+(FUE/5) m/turno
 * - Escalada:           (FUE×0.5)+(AGI×0.5)+(DES×0.5) m/turno
 * - Natación:           (FUE×0.5)+(AGI×0.5)+(RES×0.5) m/turno
 */

function n(v) {
  return Number(v) || 0
}

/** Redondeo a 1 decimal (quita .0). */
export function formatDerivedStat(value) {
  const r = Math.round(n(value) * 10) / 10
  return Number.isInteger(r) ? String(r) : r.toFixed(1)
}

export function computeDerivedPVCK(stats, level = 1, clanBonusPV = 0, clanBonusCK = 0) {
  const FUE = n(stats?.fuerza)
  const AGI = n(stats?.agilidad)
  const RES = n(stats?.resistencia)
  const VOL = n(stats?.voluntad)
  const CdC = n(stats?.controlChakra)
  const CON = n(stats?.concentracion)
  const INT = n(stats?.inteligencia)
  const lvl = Math.max(1, Number(level) || 1)

  const pvBase = 50 + FUE * 1.5 + AGI * 1.5 + RES * 2
  const PV = Math.floor(pvBase * (1 + lvl / 20) + Number(clanBonusPV || 0))
  const CK = Math.floor(
    100 + INT * 1.5 + VOL * 1.5 + CdC + CON + lvl / 10 + Number(clanBonusCK || 0)
  )
  return { PV, CK }
}

/**
 * @param {{ fuerza?: number, destreza?: number, agilidad?: number, resistencia?: number, voluntad?: number, concentracion?: number, inteligencia?: number }} stats
 */
export function computeCombatAttributes(stats = {}) {
  const FUE = n(stats.fuerza)
  const DES = n(stats.destreza)
  const AGI = n(stats.agilidad)
  const RES = n(stats.resistencia)
  const VOL = n(stats.voluntad)
  const CON = n(stats.concentracion)
  const INT = n(stats.inteligencia)

  return {
    precision: DES * 2 + FUE / 5,
    aim: DES * 2 + CON / 5,
    endurance: VOL * 2 + RES / 2,
    focus: CON * 2 + VOL / 5 + INT / 5,
    verticalJump: FUE * 0.5 + AGI * 1.5,
    horizontalJump: FUE * 0.3 + AGI * 2,
    maxWeight: FUE * 5 + RES * 2,
    runSpeed: AGI * 2 + FUE / 5,
    climb: FUE * 0.5 + AGI * 0.5 + DES * 0.5,
    swim: FUE * 0.5 + AGI * 0.5 + RES * 0.5,
  }
}

/**
 * Meta UI para atributos de combate. Iconos en /assets/attributes/att_*.png
 * `parentStat` = clave EN del primario anfitrión (stat dominante de la fórmula).
 */
export const COMBAT_ATTR_META = [
  {
    key: 'maxWeight',
    label: 'Peso máximo',
    short: 'PES',
    unit: 'kg',
    parentStat: 'strength',
    icon: '/assets/attributes/att_maxWeight.png',
  },
  {
    key: 'precision',
    label: 'Precisión',
    short: 'PRE',
    unit: '',
    hint: 'Acierto de ataques cuerpo a cuerpo',
    parentStat: 'dexterity',
    icon: '/assets/attributes/att_precision.png',
  },
  {
    key: 'aim',
    label: 'Puntería',
    short: 'PUN',
    unit: '',
    hint: 'Acierto con proyectiles',
    parentStat: 'dexterity',
    icon: '/assets/attributes/att_aim.png',
  },
  {
    key: 'climb',
    label: 'Escalada',
    short: 'ESC',
    unit: 'm/turno',
    parentStat: 'dexterity',
    icon: '/assets/attributes/att_climb.png',
  },
  {
    key: 'verticalJump',
    label: 'Salto vertical',
    short: 'SV',
    unit: 'm',
    parentStat: 'agility',
    icon: '/assets/attributes/att_verticalJump.png',
  },
  {
    key: 'horizontalJump',
    label: 'Salto horizontal',
    short: 'SH',
    unit: 'm',
    parentStat: 'agility',
    icon: '/assets/attributes/att_horizontalJump.png',
  },
  {
    key: 'runSpeed',
    label: 'Velocidad',
    short: 'VEL',
    unit: 'm/turno',
    parentStat: 'agility',
    icon: '/assets/attributes/att_runSpeed.png',
  },
  {
    key: 'swim',
    label: 'Natación',
    short: 'NAT',
    unit: 'm/turno',
    parentStat: 'resistance',
    icon: '/assets/attributes/att_swim.png',
  },
  {
    key: 'endurance',
    label: 'Aguante',
    short: 'AGU',
    unit: '',
    hint: 'Resistencia a estados alterados',
    parentStat: 'willpower',
    icon: '/assets/attributes/att_endurance.png',
  },
  {
    key: 'focus',
    label: 'Enfoque',
    short: 'ENF',
    unit: '',
    parentStat: 'concentration',
    icon: '/assets/attributes/att_focus.png',
  },
]

/** Derivados anidados bajo un primario (clave EN). */
export function combatAttrsForParent(parentStat) {
  return COMBAT_ATTR_META.filter((m) => m.parentStat === parentStat)
}

/** Stats de inventario (base + bonus) → claves del wizard. */
export function combatStatsFromCharacter(char = {}) {
  const s = char?.stats || {}
  const total = (key) => Number(s[key]?.base ?? 0) + Number(s[key]?.bonus ?? 0)
  return {
    fuerza: total('strength'),
    destreza: total('dexterity'),
    agilidad: total('agility'),
    resistencia: total('resistance'),
    voluntad: total('willpower'),
    controlChakra: total('chakraControl'),
    concentracion: total('concentration'),
    inteligencia: total('intelligence'),
  }
}

export function previewLevelUpPVCK(char = {}, currentLevel = 1) {
  const stats = combatStatsFromCharacter(char)
  const level = Math.max(1, Number(currentLevel) || 1)
  const current = computeDerivedPVCK(stats, level)
  const next = computeDerivedPVCK(stats, level + 1)
  return {
    current,
    next,
    delta: {
      PV: next.PV - current.PV,
      CK: next.CK - current.CK,
    },
  }
}
